import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/_new/Button';
import { Dialog, DialogAction } from 'components/_new/Dialog';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useAuth } from 'contexts/authContext';
import { InputTextArea } from 'components/_new/InputTextArea';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';

type ArticlePresenterFeaebackProps = {
	articleContentId: number;
	bump?: number;
	onSuccess?: () => void;
	big?: boolean;
	iconOnly?: boolean;
	applicationId?: number;
};

export const ArticlePresenterFeedback = ({
	articleContentId,
	bump = 0,
	big = false,
	iconOnly = false,
	applicationId,
}: ArticlePresenterFeaebackProps) => {
	const { t } = useTranslation();

	const { toastRef } = useGlobal();
	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();

	const [afterFeedbackModalOpen, setAfterFeedbackModalOpen] = useState(false);
	const queryClient = useQueryClient();

	const { handleSubmit, register, reset } = useForm({ defaultValues: { comment: '' } });
	const handleSubmitForm = handleSubmit(({ comment }) => {
		addRatingCommentMutate(comment);
	});

	const {
		data: currentRating,
		isLoading: currentRatingLoading,
		refetch: currentRatingRefetch,
	} = useQuery(
		['KNOWLEDGE_BASE_ARTICLE_RATING', { userId: Number(currentUser?.id), articleContentId }],
		() =>
			knowledgeBaseApi.application.getArticleContentRating(
				articleContentId,
				Number(applicationId || currentApplicationData?.id)
			),
		{
			initialData: null,
			enabled: true,
			onSuccess: (data) => {
				if (data?.comment && String(data.comment).length > 0) {
					reset({ comment: data.comment });
				}
			},
		}
	);

	useEffect(() => {
		if (bump > 0) {
			currentRatingRefetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bump]);

	const { mutate: addRatingMutate, isLoading: addRatingLoading } = useMutation(
		(rating: number) =>
			knowledgeBaseApi.application.modifyArticleContentRating(
				articleContentId,
				Number(applicationId || currentApplicationData?.id),
				rating
			),
		{
			onMutate: async (newRating) => {
				const queryKey = [
					'KNOWLEDGE_BASE_ARTICLE_RATING',
					{ userId: Number(currentUser?.id), articleContentId },
				];
				await queryClient.cancelQueries(queryKey);
				const previousRating = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: any) => {
					return {
						...old,
						rating: newRating,
					};
				});
				return { previousRating, queryKey };
			},
			onError: (err, newRating, context: any) => {
				if (context?.previousRating) {
					queryClient.setQueryData(context.queryKey, context.previousRating);
				}
			},
			onSettled: (data, error, variables, context: any) => {
				if (context?.queryKey) {
					queryClient.invalidateQueries({ queryKey: context.queryKey });
				}
			},
			onSuccess: (data, rating) => {
				if (rating === 0) {
					toastRef.current?.show({
						severity: 'info',
						life: 5000,
						summary: t('misc.success'),
						detail: t('knowledgeBase.rating.afterRemoveRatingInfo'),
					});
				} else {
					toastRef.current?.show({
						severity: 'success',
						life: 5000,
						summary: t('misc.success'),
						detail: t('knowledgeBase.rating.addRatingSuccess'),
					});
					setAfterFeedbackModalOpen(true);
				}
				currentRatingRefetch();
			},
		}
	);

	const handleRateUpClick = useCallback(() => {
		const isRatingUp = currentRating && currentRating.rating === 1;
		addRatingMutate(isRatingUp ? 0 : 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentRating]);

	const handleRateDownClick = useCallback(() => {
		const isRatingDown = currentRating && currentRating.rating === -1;
		addRatingMutate(isRatingDown ? 0 : -1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentRating]);

	const { mutate: addRatingCommentMutate, isLoading: addRatingCommentLoading } = useMutation(
		(comment: string) =>
			knowledgeBaseApi.application.addArticleContentRatingComment(
				articleContentId,
				Number(applicationId || currentApplicationData?.id),
				comment
			),
		{
			onSuccess: (data) => {
				currentRatingRefetch();
				setAfterFeedbackModalOpen(false);
				if (data?.comment && String(data.comment).length > 0) {
					toastRef.current?.show({
						severity: 'success',
						life: 5000,
						summary: t('misc.success'),
						detail: t('knowledgeBase.rating.addCommentSuccess'),
					});
				}
			},
		}
	);

	return (
		<>
			<DialogAction
				title={t('knowledgeBase.rating.thanks1')}
				visible={afterFeedbackModalOpen}
				onHide={() => {
					setAfterFeedbackModalOpen(false);
				}}
				actions={[
					{
						key: 'save',
						label: t('actions.save'),
						submit: true,
						form: 'rating-comment-form',
						loading: addRatingCommentLoading,
					},
				]}
			>
				<p>{t('knowledgeBase.rating.thanks2')}</p>
				<form
					id="rating-comment-form"
					onSubmit={handleSubmitForm}
					className="w-full flex flex-column gap-1 mt-2"
				>
					<InputTextArea
						{...register('comment')}
						placeholder={t('knowledgeBase.rating.commentPlaceholder')}
					/>
				</form>
			</DialogAction>

			{big ? (
				<div className="flex flex-column gap-1 align-items-center">
					<span>{t('knowledgeBase.rating.question')}</span>
					<span className="inline-flex gap-2">
						<Button
							label={t('misc.yes')}
							onClick={handleRateUpClick}
							title={t('knowledgeBase.rating.rateUp')}
							disabled={currentRatingLoading || addRatingLoading}
							variant={!(currentRating?.rating === 1) ? 'grey-text' : 'primary-text'}
							variantSize="lg"
							icon="thumbs-up"
							iconSet="fa"
							iconStyle="regular"
							iconClassName="text-3xl"
							iconPosition="right"
							className="p-1"
						/>
						<Button
							label={t('misc.no')}
							onClick={handleRateDownClick}
							title={t('knowledgeBase.rating.rateDown')}
							disabled={currentRatingLoading || addRatingLoading}
							variant={!(currentRating?.rating === -1) ? 'grey-text' : 'primary-text'}
							variantSize="lg"
							icon="thumbs-down"
							iconSet="fa"
							iconStyle="regular"
							iconClassName="text-3xl"
							className="p-1"
						/>
					</span>
				</div>
			) : (
				<div className="flex flex-row gap-1 align-items-center">
					{!iconOnly && (
						<div className="h-full p-1">
							<span className="opacity-9 text-sm-btn">{t('knowledgeBase.rating.rate')}:</span>
						</div>
					)}
					<span className="inline-flex gap-0">
						<Button
							label={t('misc.yes')}
							onClick={handleRateUpClick}
							title={t('knowledgeBase.rating.rateUp')}
							disabled={currentRatingLoading || addRatingLoading}
							variant={!(currentRating?.rating === 1) ? 'grey-text' : 'primary-text'}
							iconOnly
							icon="thumbs-up"
							iconSet="fa"
							iconStyle="regular"
							iconPosition="right"
							iconClassName="text-lg"
						/>
						<Button
							label={t('misc.no')}
							onClick={handleRateDownClick}
							title={t('knowledgeBase.rating.rateDown')}
							disabled={currentRatingLoading || addRatingLoading}
							variant={!(currentRating?.rating === -1) ? 'grey-text' : 'primary-text'}
							iconOnly
							icon="thumbs-down"
							iconSet="fa"
							iconStyle="regular"
							iconClassName="text-lg"
						/>
					</span>
				</div>
			)}
		</>
	);
};
